import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Check, Wallet, CalendarDays, ArrowLeft, ImagePlus, Phone, Fuel, CalendarCheck } from "lucide-react";
import RequestModal from "@/components/RequestModal";
import ConsultationModal from "@/components/ConsultationModal";
import { useBenefits, benefitsFor, BENEFIT_ICONS } from "@/hooks/useBenefits";
import SalePrice from "@/components/SalePrice";
import Breadcrumbs from "@/components/Breadcrumbs";
import JsonLd from "@/components/JsonLd";
import { categoryById, STATUS_LABELS, STATUS_STYLES } from "@/data/categories";
import { useCategories } from "@/hooks/useCategories";
import { useSettings } from "@/hooks/useSettings";
import { useSEO } from "@/hooks/useSEO";
import { API, photoSrc, telHref } from "@/lib/api";

export default function VehiclePage() {
  const { id } = useParams();
  const [vehicle, setVehicle] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [active, setActive] = useState(0);
  const [reserveOpen, setReserveOpen] = useState(false);
  const [consultOpen, setConsultOpen] = useState(false);
  const meta = useCategories();
  const settings = useSettings();
  const phone = settings.phone || "591 063 123";
  const benefits = benefitsFor(useBenefits(), { categoryId: vehicle?.category_id, vehicleId: vehicle?.id });

  useEffect(() => {
    window.scrollTo(0, 0);
    setActive(0);
    setVehicle(null);
    setNotFound(false);
    axios
      .get(`${API}/vehicles/${id}`)
      .then((r) => setVehicle(r.data))
      .catch(() => setNotFound(true));
  }, [id]);

  const category = vehicle ? meta[vehicle.category_id] || categoryById(vehicle.category_id) : null;

  useSEO({
    title: vehicle && category
      ? `${vehicle.public_title || `${category.name} ${vehicle.year}`} ქირით ტაქსისთვის — ${vehicle.price} ₾/დღე | taxipark.ge`
      : "taxipark.ge",
    description: vehicle && category
      ? `${category.name} ${vehicle.year} — ${vehicle.price} ₾ დღიურად, დეპოზიტი ${vehicle.deposit} ₾. ${category.years} კატეგორია ტაქსისთვის თბილისში. ${vehicle.description || category.tagline}`
      : undefined,
    path: `/vehicle/${id}`,
  });

  if (notFound) {
    return (
      <main className="pt-40 pb-24 text-center" data-testid="vehicle-not-found">
        <p className="text-gray-400 text-lg">ავტომობილი ვერ მოიძებნა ან უკვე აღარაა ხელმისაწვდომი.</p>
        <Link to="/catalog" data-testid="vehicle-not-found-back" className="btn-gold mt-8">კატალოგში დაბრუნება</Link>
      </main>
    );
  }

  if (!vehicle) {
    return <main className="pt-40 pb-24 text-center text-gray-500">იტვირთება...</main>;
  }

  if (vehicle.status === "rented" || vehicle.status === "service") {
    return (
      <main className="pt-40 pb-24 text-center px-4" data-testid="vehicle-unavailable-page">
        <span className={`inline-block px-4 py-1.5 rounded-full text-sm font-semibold border ${STATUS_STYLES[vehicle.status]}`}>
          {STATUS_LABELS[vehicle.status]}
        </span>
        <h1 className="text-3xl sm:text-4xl font-bold text-white mt-6">
          {categoryById(vehicle.category_id)?.name} {vehicle.year}
        </h1>
        <p className="text-gray-400 mt-4 max-w-md mx-auto leading-relaxed">
          {vehicle.status === "rented"
            ? "ეს ავტომობილი ამჟამად გაქირავებულია. იხილე სხვა თავისუფალი მანქანები ამ კატეგორიაში."
            : "ეს ავტომობილი ამჟამად სერვისშია. იხილე სხვა თავისუფალი მანქანები ამ კატეგორიაში."}
        </p>
        <Link to={`/category/${vehicle.category_id}`} data-testid="vehicle-unavailable-back" className="btn-gold mt-8">
          კატეგორიის ნახვა
        </Link>
      </main>
    );
  }

  const photos = vehicle.photos || [];
  const current = photos[Math.min(active, photos.length - 1)];

  const conditions = vehicle.conditions
    ? vehicle.conditions.split("\n").map((s) => s.trim()).filter(Boolean)
    : category?.features || [];

  return (
    <main data-testid="vehicle-page" className="pt-28 pb-8">
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "Product",
        name: vehicle.public_title || `${category?.name} ${vehicle.year}`,
        image: photos.map((p) => `https://taxipark.ge${p.url}`),
        description: vehicle.description || category?.tagline || "",
        offers: {
          "@type": "Offer",
          price: vehicle.sale_enabled && vehicle.sale_price ? vehicle.sale_price : vehicle.price,
          priceCurrency: "GEL",
          availability: vehicle.status === "available" ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
          url: `https://taxipark.ge/vehicle/${vehicle.id}`,
        },
      }} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <Breadcrumbs items={[
          { name: "მთავარი", path: "/" },
          { name: "კატალოგი", path: "/catalog" },
          { name: `${category?.name || ""} ${category?.years || ""}`.trim(), path: `/category/${vehicle.category_id}` },
          { name: vehicle.public_title || `${category?.name} ${vehicle.year}`, path: `/vehicle/${vehicle.id}` },
        ]} />
        <Link to={`/category/${vehicle.category_id}`} data-testid="vehicle-back-link" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors mb-8">
          <ArrowLeft className="w-4 h-4" /> {category?.name} {category?.years}
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="rounded-2xl overflow-hidden border border-white/10 bg-[#0D0F14]">
              {current ? (
                <img src={photoSrc(current)} alt={`${category?.name} ${vehicle.year} — ტაქსის ავტომობილი ქირით, ფოტო ${Math.min(active, photos.length - 1) + 1}`} className="w-full h-72 sm:h-96 object-cover" data-testid="vehicle-main-photo" />
              ) : (
                <div className="w-full h-72 sm:h-96 flex flex-col items-center justify-center gap-3 text-gray-500" data-testid="vehicle-main-photo">
                  <ImagePlus className="w-10 h-10" />
                  <span className="text-sm">ფოტოები მალე დაემატება</span>
                </div>
              )}
            </div>
            {photos.length > 1 && (
              <div className="grid grid-cols-4 gap-3 mt-4" data-testid="vehicle-thumbs">
                {photos.map((p, i) => (
                  <button
                    key={p.id}
                    data-testid={`vehicle-thumb-${i}`}
                    onClick={() => setActive(i)}
                    className={`h-20 sm:h-24 rounded-xl overflow-hidden border transition-colors ${
                      i === active ? "border-[#D4AF37]" : "border-white/15 hover:border-white/40"
                    }`}
                  >
                    <img src={photoSrc(p)} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}>
            <span
              data-testid="vehicle-status"
              className={`inline-block px-3 py-1 rounded-full text-xs font-semibold border ${STATUS_STYLES[vehicle.status]}`}
            >
              {STATUS_LABELS[vehicle.status]}
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-white mt-4" data-testid="vehicle-title">
              {vehicle.public_title || (
                <>
                  {category?.name} <span className="text-[#E5C378]">{vehicle.year}</span>
                </>
              )}
            </h1>
            {vehicle.public_title && category && (
              <p className="text-base text-gray-400 mt-2">{category.name} {vehicle.year} · {category.years}</p>
            )}
            {vehicle.plate && (
              <p className="text-sm text-gray-500 mt-2" data-testid="vehicle-plate">სახელმწიფო ნომერი: <b className="text-gray-300">{vehicle.plate}</b></p>
            )}
            {vehicle.description && (
              <p className="text-lg text-gray-400 mt-4 leading-relaxed" data-testid="vehicle-description">{vehicle.description}</p>
            )}

            <div className="grid grid-cols-2 gap-4 mt-8">
              <div className="card-lux p-5" data-testid="vehicle-price-box">
                <p className="text-xs uppercase tracking-widest text-gray-500 mb-1 flex items-center gap-1.5">
                  <CalendarDays className="w-3.5 h-3.5" /> დღიური ფასი
                </p>
                <SalePrice original={`${vehicle.price} ₾`} sale={vehicle.sale_price ? `${vehicle.sale_price} ₾` : null} enabled={vehicle.sale_enabled} suffix="" size="lg" />
              </div>
              <div className="card-lux p-5" data-testid="vehicle-deposit-box">
                <p className="text-xs uppercase tracking-widest text-gray-500 mb-1 flex items-center gap-1.5">
                  <Wallet className="w-3.5 h-3.5" /> დეპოზიტი
                </p>
                <p className="text-2xl font-bold text-white">{vehicle.deposit} ₾</p>
              </div>
            </div>

            {vehicle.fuel_type && (
              <p className="mt-5 text-sm text-gray-300 flex items-center gap-2" data-testid="vehicle-fuel">
                <Fuel className="w-4 h-4 text-[#D4AF37]" /> საწვავი: <b className="text-white">{vehicle.fuel_type}</b>
              </p>
            )}

            {benefits.length > 0 && (
              <div className="mt-10" data-testid="vehicle-benefits">
                <h2 className="text-lg font-semibold text-white mb-4">შეღავათები</h2>
                <div className="space-y-3">
                  {benefits.map((b) => {
                    const Icon = BENEFIT_ICONS[b.icon] || BENEFIT_ICONS.star;
                    return (
                      <div key={b.id} className="flex items-start gap-4 p-4 rounded-xl bg-[#D4AF37]/6 border border-[#D4AF37]/20" data-testid={`vehicle-benefit-${b.id}`}>
                        <span className="w-10 h-10 rounded-xl bg-[#D4AF37]/12 flex items-center justify-center shrink-0">
                          <Icon className="w-5 h-5 text-[#E5C378]" />
                        </span>
                        <div>
                          <h3 className="text-sm font-semibold text-[#E5C378]">{b.title}</h3>
                          {b.description && <p className="text-sm text-gray-400 mt-1 leading-relaxed">{b.description}</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {category && (
              <>
                <h2 className="text-lg font-semibold text-white mt-10 mb-4">ქირის პირობები</h2>
                <ul className="space-y-3" data-testid="vehicle-features-list">
                  {conditions.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-gray-300 text-sm leading-relaxed">
                      <Check className="w-5 h-5 text-[#D4AF37] shrink-0 mt-0.5" />
                      {f}
                    </li>
                  ))}
                </ul>
                <div className="mt-8 p-5 rounded-xl bg-[#D4AF37]/8 border border-[#D4AF37]/25" data-testid="vehicle-note">
                  <p className="text-sm text-[#E5C378] leading-relaxed">{category.note}</p>
                </div>
              </>
            )}

            <div className="flex flex-col sm:flex-row gap-4 mt-10">
              {vehicle.status === "available" ? (
                <button onClick={() => setReserveOpen(true)} data-testid="vehicle-rent-cta" className="btn-gold flex-1 text-base">
                  <CalendarCheck className="w-5 h-5" /> დაჯავშნა
                </button>
              ) : (
                <div data-testid="vehicle-rent-cta" className="btn-gold flex-1 text-base opacity-40 cursor-not-allowed pointer-events-none" aria-disabled="true">
                  {STATUS_LABELS[vehicle.status]}
                </div>
              )}
              <a href={telHref(phone)} data-testid="vehicle-call-cta" className="btn-ghost flex-1 text-base">
                <Phone className="w-5 h-5" /> კონსულტაცია
              </a>
            </div>
            <button
              onClick={() => setConsultOpen(true)}
              data-testid="vehicle-consult-request-btn"
              className="mt-4 w-full text-sm text-gray-400 hover:text-[#E5C378] underline-offset-4 hover:underline transition-colors"
            >
              ვერ პასუხობ ზარს? მოითხოვე ზარი — ჩვენ დაგირეკავთ
            </button>
          </motion.div>
        </div>
      </div>
      {reserveOpen && (
        <RequestModal type="reservation" vehicle={vehicle} category={category} onClose={() => setReserveOpen(false)} />
      )}
      <ConsultationModal open={consultOpen} onClose={() => setConsultOpen(false)} />
    </main>
  );
}
